import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../auth/AuthProvider'
import { formatDateShort } from '../lib/datetime'

interface Code {
  code: string
  note: string | null
  max_uses: number
  uses: number
  expires_at: string | null
  created_at: string
}

export function AdminCodes() {
  const { profile } = useAuth()
  const [codes, setCodes] = useState<Code[]>([])
  const [note, setNote] = useState('')
  const [maxUses, setMaxUses] = useState(1)
  const [fresh, setFresh] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('group_invite_codes')
      .select('code, note, max_uses, uses, expires_at, created_at')
      .order('created_at', { ascending: false })
    if (error) setError(error.message)
    else setCodes((data as Code[]) ?? [])
    setLoading(false)
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  if (profile && profile.role !== 'admin') {
    return (
      <div className="page">
        <div className="notice notice--error">Den här sidan är bara för admin.</div>
      </div>
    )
  }

  async function generate(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    setCopied(false)

    const { data, error } = await supabase.rpc('generate_group_code', {
      p_note: note.trim() || null,
      p_max_uses: maxUses,
      p_expires_at: null,
    })

    setBusy(false)
    if (error) { setError(error.message); return }

    setFresh(data as string)
    setNote('')
    await load()
  }

  function status(c: Code): string {
    if (c.expires_at && new Date(c.expires_at) < new Date()) return 'utgången'
    if (c.uses >= c.max_uses) return 'använd'
    return `${c.max_uses - c.uses} kvar`
  }

  return (
    <div className="page stack">
      <div>
        <p className="eyebrow">Admin</p>
        <h1>Koder för nya gäng</h1>
        <p className="muted">
          Gäng skapas inte fritt. Den som vill starta ett mejlar
          scratch@majortipset.com, du genererar en kod här och skickar den i svaret.
        </p>
      </div>

      {error && <div className="notice notice--error">{error}</div>}

      {fresh && (
        <div className="card stack">
          <p className="eyebrow">Ny kod</p>
          <p style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', color: 'var(--gold)', letterSpacing: '0.08em' }}>
            {fresh}
          </p>
          <div>
            <button
              onClick={async () => {
                await navigator.clipboard.writeText(fresh)
                setCopied(true)
                setTimeout(() => setCopied(false), 2500)
              }}
            >
              {copied ? 'Kopierad' : 'Kopiera'}
            </button>
          </div>
        </div>
      )}

      <form className="card stack" onSubmit={generate}>
        <h2>Generera</h2>
        <div>
          <label htmlFor="note">Vem får den?</label>
          <input
            id="note"
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Kalle, via scratch@majortipset.com"
          />
          <p className="muted">
            Anteckningen är till för dig. Utan den går det inte att se vem som fick
            vilken kod när något ser konstigt ut.
          </p>
        </div>
        <div style={{ maxWidth: 160 }}>
          <label htmlFor="uses">Antal användningar</label>
          <input
            id="uses"
            type="number"
            min={1}
            max={20}
            value={maxUses}
            onChange={(e) => setMaxUses(Number(e.target.value))}
          />
        </div>
        <button className="primary" type="submit" disabled={busy}>
          {busy ? 'Genererar…' : 'Generera kod'}
        </button>
      </form>

      {loading && <p className="muted">Hämtar…</p>}

      {codes.length > 0 && (
        <section className="stack">
          <h2>Utdelade koder</h2>
          <div className="board">
            {codes.map((c) => (
              <div key={c.code} className="marathon__row">
                <span className="board__name" style={{ fontFamily: 'var(--font-display)' }}>
                  {c.code}
                </span>
                <span className="marathon__cell" style={{ minWidth: 140, textAlign: 'left' }}>
                  {c.note ?? '—'}
                </span>
                <span className="marathon__cell">{formatDateShort(c.created_at)}</span>
                <span className="marathon__score">{status(c)}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
